import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { brokerApi } from "@/hooks/useBrokerApi";

const STATUSES = ["pending_driver_confirmation", "confirmed", "approved", "disputed"];

const StatusFilterBar = ({ filter, onChange }) => (
  <div className="flex flex-wrap gap-2 mb-5">
    <button onClick={() => onChange("")} data-testid="service-logs-filter-all" className={`text-xs font-bold px-3 py-1.5 rounded-full border ${filter === "" ? "bg-rr-navy text-white border-rr-navy" : "border-rr-line text-rr-muted"}`}>
      All
    </button>
    {STATUSES.map((s) => (
      <button key={s} onClick={() => onChange(s)} data-testid={`service-logs-filter-${s}`} className={`text-xs font-bold px-3 py-1.5 rounded-full border capitalize ${filter === s ? "bg-rr-navy text-white border-rr-navy" : "border-rr-line text-rr-muted"}`}>
        {s.replace(/_/g, " ")}
      </button>
    ))}
  </div>
);

const ServiceLogRow = ({ log: l, onPatch }) => (
  <div data-testid={`service-log-row-${l.id}`} className="bg-white border border-rr-line rounded-xl p-5">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="font-heading font-bold text-rr-navy">{l.origin} → {l.destination}</p>
        <p className="text-xs text-rr-muted mt-0.5">{l.driver_name || "Unassigned"}{l.mileage ? ` · ${l.mileage} mi` : ""} · ${l.compensation ?? 0}</p>
      </div>
      <select
        value={l.status}
        onChange={(e) => onPatch(l.id, { status: e.target.value }, "Status updated.")}
        data-testid={`service-log-status-select-${l.id}`}
        className="border border-rr-line rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-rr-orange capitalize"
      >
        {STATUSES.map((s) => (<option key={s} value={s}>{s.replace(/_/g, " ")}</option>))}
      </select>
    </div>
    <div className="grid sm:grid-cols-2 gap-3 mt-4">
      <input
        type="number"
        defaultValue={l.approved_expenses}
        onBlur={(e) => Number(e.target.value) !== l.approved_expenses && onPatch(l.id, { approved_expenses: Number(e.target.value) || 0 })}
        placeholder="Approved expenses ($)"
        data-testid={`service-log-expenses-input-${l.id}`}
        className="border border-rr-line rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-rr-orange"
      />
      <input
        type="number"
        defaultValue={l.detention}
        onBlur={(e) => Number(e.target.value) !== l.detention && onPatch(l.id, { detention: Number(e.target.value) || 0 })}
        placeholder="Detention ($)"
        data-testid={`service-log-detention-input-${l.id}`}
        className="border border-rr-line rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-rr-orange"
      />
    </div>
    <textarea
      defaultValue={l.pod_note || ""}
      onBlur={(e) => e.target.value !== (l.pod_note || "") && onPatch(l.id, { pod_note: e.target.value })}
      placeholder="BOL / POD / receipts notes"
      rows={2}
      data-testid={`service-log-notes-input-${l.id}`}
      className="w-full mt-3 border border-rr-line rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-rr-orange resize-none"
    />
    {l.invoiced && <p className="text-[11px] text-rr-green font-bold mt-2">Invoiced</p>}
  </div>
);

export const BrokerServiceLogsTab = () => {
  const [logs, setLogs] = useState(null);
  const [filter, setFilter] = useState("");

  const load = (status) => brokerApi.get("/service-logs", status ? { status } : undefined).then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  useEffect(() => { load(filter || undefined); }, [filter]);

  const patch = async (id, body, msg) => {
    try {
      await brokerApi.patch(`/service-logs/${id}`, body);
      if (msg) toast.success(msg);
      load(filter || undefined);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update this service log.");
    }
  };

  if (!logs) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-rr-orange" /></div>;

  return (
    <div data-testid="broker-service-logs-tab">
      <h2 className="font-heading text-xl font-bold text-rr-navy mb-5">Service Logs</h2>
      <StatusFilterBar filter={filter} onChange={setFilter} />
      {logs.length === 0 && (
        <p data-testid="service-logs-empty-state" className="text-rr-muted text-sm">No service logs yet — they're created automatically when a load is marked delivered.</p>
      )}
      <div className="space-y-3">
        {logs.map((l) => (<ServiceLogRow key={l.id} log={l} onPatch={patch} />))}
      </div>
    </div>
  );
};
